import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";

import { useRedux } from "../../../hooks";
import { submitStepConfig } from "../../../redux/process/actions";
import {
  WorkflowStepData,
  StepStatus,
  StepType,
  SubmitStepPayload,
  FeedbackEntry,
  FileDetails,
} from "../../../types/process";
import StepConfigurationForm from "./StepConfigurationForm";
import CompletionAnimation from "./CompletionAnimation";
import {
  downloadFile,
  isAgentProxyUrl,
} from "../../../services/process/downloadFile";
import logger from "../../../utils/logger";

interface StepDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: WorkflowStepData | null;
  categoryId: string | null;
  processId: string | null;
}

const StepDetailModal: React.FC<StepDetailModalProps> = ({
  isOpen,
  onClose,
  step,
  categoryId,
  processId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { dispatch } = useRedux();
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  if (!isOpen || !step || !categoryId || !processId) return null;

  const handleSaveConfiguration = async (payload: SubmitStepPayload) => {
    // Check if the payload contains File objects that would cause Redux state mutations
    const hasFileObjects = (obj: any): boolean => {
      if (obj instanceof File) return true;
      if (typeof obj === "object" && obj !== null) {
        return Object.values(obj).some(hasFileObjects);
      }
      return false;
    };

    if (hasFileObjects(payload)) {
      // If payload contains File objects, call API directly to avoid Redux state mutations
      try {
        const { submitStepConfiguration } =
          await import("../../../services/process/api");
        await submitStepConfiguration(payload);

        // After successful API call, fetch fresh data
        const { fetchWorkflows } =
          await import("../../../redux/process/actions");
        dispatch(fetchWorkflows());
      } catch (error) {
        logger.error("Error submitting step configuration:", error);
        // You might want to show an error message to the user here
        return;
      }
    } else {
      // No File objects, safe to use Redux
      dispatch(submitStepConfig(payload));
    }

    setShowCompletionAnimation(true);
    setIsEditMode(false);
    setTimeout(() => {
      if (!isEditMode) {
        onClose();
      }
      setShowCompletionAnimation(false);
    }, 1500);
  };

  const renderStepDetails = (details: any) => {
    if (typeof details === "string") {
      return <Typography variant="body1">{details}</Typography>;
    }
    if (
      typeof details === "object" &&
      details !== null &&
      Object.keys(details).length > 0
    ) {
      return (
        <List>
          {Object.entries(details).map(([key, value]) => (
            <ListItem key={key} disablePadding>
              <ListItemText
                primary={key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
                secondary={String(value)}
              />
            </ListItem>
          ))}
        </List>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{ fontStyle: "italic", color: "text.secondary" }}
      >
        {t("no_details_available")}
      </Typography>
    );
  };

  const currentKnowledgeData = step.currentConfig?.knowledgeData;
  const currentConfigType = step.currentConfig?.type;
  const actualStepType = currentConfigType || step.type;

  const selectedAppDisplay =
    step.currentConfig?.selectedAppId &&
    step.config?.availableApps?.find(
      (app) => app.id === step.currentConfig?.selectedAppId,
    );

  const handleFileDownload = async (file: FileDetails, e: React.MouseEvent) => {
    e.preventDefault();

    if (!file.url) return;

    const fileId = file.id || file.name;

    // Check if this file is already being downloaded
    if (downloadingFiles.has(fileId)) return;

    try {
      // Add to downloading set
      setDownloadingFiles((prev) => new Set(prev).add(fileId));

      // Download the file
      await downloadFile(file.url, file.name);
    } catch (error) {
      logger.error("Failed to download file:", error);
      // You could show an error notification here
    } finally {
      // Remove from downloading set
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const renderFileList = (files?: FileDetails[], listTitle?: string) => {
    if (!files || files.length === 0) return null;
    return (
      <Box sx={{ mt: 1 }}>
        {listTitle && (
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            {listTitle}
          </Typography>
        )}
        <List dense>
          {files.map((file) => {
            const fileId = file.id || file.name;
            const isDownloading = downloadingFiles.has(fileId);
            const isAgentProxy = file.url ? isAgentProxyUrl(file.url) : false;

            return (
              <ListItem key={fileId} sx={{ py: 0.5 }}>
                {isDownloading ? (
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                ) : (
                  <AttachFileIcon
                    sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                  />
                )}
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      component={isAgentProxy ? "span" : "a"}
                      href={!isAgentProxy && file.url ? file.url : undefined}
                      target={!isAgentProxy ? "_blank" : undefined}
                      rel={!isAgentProxy ? "noopener noreferrer" : undefined}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        cursor: file.url ? "pointer" : "default",
                        "&:hover": {
                          textDecoration: file.url ? "underline" : "none",
                        },
                      }}
                      onClick={
                        file.url
                          ? (e: React.MouseEvent) => {
                              if (isAgentProxy) {
                                handleFileDownload(file, e);
                              } else if (!file.url) {
                                e.preventDefault();
                              }
                            }
                          : undefined
                      }
                    >
                      {file.name}
                    </Typography>
                  }
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  const renderFeedbackHistory = (feedbackHistory?: FeedbackEntry[]) => {
    if (!feedbackHistory || feedbackHistory.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          {t("no_feedback_history")}
        </Typography>
      );
    }
    return (
      <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
        {feedbackHistory
          .slice()
          .reverse()
          .map((entry) => (
            <Paper key={entry.id} sx={{ p: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {entry.text}
              </Typography>
              {renderFileList(entry.attachments, t("attachments"))}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "right" }}
              >
                - {entry.author} {t("on")}{" "}
                {new Date(entry.date).toLocaleDateString()}
              </Typography>
            </Paper>
          ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "50vh" },
      }}
    >
      <DialogTitle>{step.name}</DialogTitle>
      <DialogContent dividers>
        {step.status === StepStatus.COMPLETE && !isEditMode && (
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: theme.palette.primary.main }}
            >
              {t("details")}:
            </Typography>
            {renderStepDetails(step.details)}

            {actualStepType === StepType.APP && selectedAppDisplay && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: theme.palette.grey[50],
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                    {t("using_app")}: <strong>{selectedAppDisplay.name}</strong>
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsEditMode(true)}
                    sx={{ color: theme.palette.primary.main }}
                    title={t("edit")}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                {step.currentConfig?.appInstallationParams &&
                  Object.keys(step.currentConfig.appInstallationParams).length >
                    0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold" }}
                      >
                        {t("configuration")}:
                      </Typography>
                      <List dense>
                        {Object.entries(
                          step.currentConfig.appInstallationParams,
                        ).map(([key, value]) => {
                          const inputSchema =
                            selectedAppDisplay.requiredInputs?.find(
                              (input) => input.name === key,
                            );
                          const displayValue =
                            inputSchema?.type === "password"
                              ? "********"
                              : String(value);
                          return (
                            <ListItem key={key} disablePadding>
                              <ListItemText
                                primary={inputSchema?.label || key}
                                secondary={displayValue}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                {!selectedAppDisplay.isInstalled && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontStyle: "italic",
                      color: theme.palette.warning.main,
                      mt: 1,
                    }}
                  >
                    {t("app_configured_not_synced")}
                  </Typography>
                )}
              </Paper>
            )}

            {actualStepType === StepType.KNOWLEDGE &&
              step.config?.isStructuredKnowledge &&
              Array.isArray(currentKnowledgeData) &&
              currentKnowledgeData.length > 0 && (
                <Paper
                  sx={{ mt: 2, p: 2, backgroundColor: theme.palette.grey[50] }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {t("provided_knowledge")}:
                  </Typography>
                  {currentKnowledgeData.map((kItem, idx) => (
                    <Box
                      key={kItem.id || idx}
                      sx={{
                        pt: idx > 0 ? 2 : 0,
                        borderTop:
                          idx > 0
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                      }}
                    >
                      {step.config?.knowledgeSchema?.map((schema) => {
                        if (schema.type === "file") {
                          const files = kItem[schema.name] as
                            | FileDetails[]
                            | undefined;
                          return (
                            <Box key={schema.name} sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                <strong>{schema.label}:</strong>
                              </Typography>
                              {files && files.length > 0 ? (
                                renderFileList(files)
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontStyle: "italic", ml: 2 }}
                                >
                                  {t("no_files")}
                                </Typography>
                              )}
                            </Box>
                          );
                        }
                        return (
                          <Typography
                            key={schema.name}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>{schema.label}:</strong>{" "}
                            {String(
                              kItem[schema.name] !== undefined
                                ? kItem[schema.name]
                                : "N/A",
                            )}
                          </Typography>
                        );
                      })}
                    </Box>
                  ))}
                </Paper>
              )}

            {actualStepType === StepType.KNOWLEDGE &&
              !step.config?.isStructuredKnowledge &&
              typeof currentKnowledgeData === "string" &&
              currentKnowledgeData && (
                <Paper
                  sx={{ mt: 2, p: 2, backgroundColor: theme.palette.grey[50] }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {t("provided_knowledge")}:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {currentKnowledgeData}
                  </Typography>
                </Paper>
              )}

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("feedback_history")}:
            </Typography>
            {renderFeedbackHistory(step.feedbackHistory)}
          </Box>
        )}

        {(step.status === StepStatus.INCOMPLETE || isEditMode) && (
          <StepConfigurationForm
            step={step}
            categoryId={categoryId}
            processId={processId}
            onSave={handleSaveConfiguration}
            onCancel={() => {
              if (isEditMode) {
                setIsEditMode(false);
              } else {
                onClose();
              }
            }}
          />
        )}
      </DialogContent>
      {step.status === StepStatus.COMPLETE && !isEditMode && (
        <DialogActions>
          <Button onClick={onClose}>{t("close")}</Button>
        </DialogActions>
      )}
      <CompletionAnimation
        show={showCompletionAnimation}
        onComplete={() => setShowCompletionAnimation(false)}
      />
    </Dialog>
  );
};

export default StepDetailModal;
